import type {
  Company,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  SyncRequest,
  SyncResponse,
  IntegrationConfig,
} from '../types/company.types';

// Mock data for development - replace with actual API calls
const mockCompanies: Company[] = [
  {
    id: 'uk-ops',
    name: 'UK Operations',
    code: 'UK',
    status: 'active',
    productInstances: [],
    financialIntegration: {
      id: 'fin-uk-1',
      companyId: 'uk-ops',
      systemType: 'Financials',
      systemName: 'Access Financials',
      connectionStatus: 'connected',
      apiEndpoint: 'https://uk.financials.com/api',
      apiKey: '***encrypted***',
      lastSync: new Date('2024-01-15T10:30:00Z'),
      lastTestDate: new Date('2024-01-15T09:00:00Z'),
      lastTestStatus: 'success',
      syncFrequency: 'hourly',
      autoSync: true,
      config: {},
      errorMessage: null,
    },
    hrIntegration: {
      id: 'hr-uk-1',
      companyId: 'uk-ops',
      systemType: 'People HR',
      systemName: 'People HR',
      connectionStatus: 'connected',
      apiEndpoint: 'https://uk.peoplehr.com/api',
      apiKey: '***encrypted***',
      lastSync: new Date('2024-01-15T08:00:00Z'),
      lastTestDate: new Date('2024-01-14T16:00:00Z'),
      lastTestStatus: 'success',
      syncFrequency: 'daily',
      autoSync: true,
      config: {},
      errorMessage: null,
    },
    activeProjects: 12,
    employeeCount: 45,
    createdAt: new Date('2023-01-01'),
    updatedAt: new Date('2024-01-15'),
    lastSync: new Date('2024-01-15T10:30:00Z'),
  },
  {
    id: 'us-ops',
    name: 'US Operations',
    code: 'US',
    status: 'active',
    productInstances: [],
    financialIntegration: {
      id: 'fin-us-1',
      companyId: 'us-ops',
      systemType: 'Financials',
      systemName: 'Access Financials',
      connectionStatus: 'not_connected',
      apiEndpoint: null,
      apiKey: null,
      lastSync: null,
      lastTestDate: null,
      lastTestStatus: null,
      syncFrequency: 'manual',
      autoSync: false,
      config: {},
      errorMessage: 'Not configured',
    },
    hrIntegration: null,
    activeProjects: 8,
    employeeCount: 32,
    createdAt: new Date('2023-06-01'),
    updatedAt: new Date('2024-01-10'),
    lastSync: null,
  },
];

export const companyService = {
  // Get all companies
  async getCompanies(): Promise<Company[]> {
    // TODO: Replace with actual API call
    // return authFetch('/api/companies');
    return Promise.resolve(mockCompanies);
  },

  // Get company by ID
  async getCompany(id: string): Promise<Company | null> {
    // TODO: Replace with actual API call
    // return authFetch(`/api/companies/${id}`);
    const company = mockCompanies.find((c) => c.id === id);
    return Promise.resolve(company || null);
  },

  // Create new company
  async createCompany(data: CreateCompanyRequest): Promise<Company> {
    // TODO: Replace with actual API call
    // return authFetch('/api/companies', { method: 'POST', body: JSON.stringify(data) });
    const newCompany: Company = {
      id: `company-${Date.now()}`,
      name: data.name,
      code: data.code,
      status: data.status || 'active',
      productInstances: [],
      financialIntegration: null,
      hrIntegration: null,
      activeProjects: 0,
      employeeCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
      lastSync: null,
    };
    mockCompanies.push(newCompany);
    return Promise.resolve(newCompany);
  },

  // Update company
  async updateCompany(id: string, data: UpdateCompanyRequest): Promise<Company> {
    // TODO: Replace with actual API call
    // return authFetch(`/api/companies/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    const company = mockCompanies.find((c) => c.id === id);
    if (!company) throw new Error('Company not found');
    
    Object.assign(company, data, { updatedAt: new Date() });
    return Promise.resolve(company);
  },

  // Delete company
  async deleteCompany(id: string): Promise<void> {
    // TODO: Replace with actual API call
    // return authFetch(`/api/companies/${id}`, { method: 'DELETE' });
    const index = mockCompanies.findIndex((c) => c.id === id);
    if (index !== -1) {
      mockCompanies.splice(index, 1);
    }
    return Promise.resolve();
  },

  // Update integration configuration
  async updateIntegration(
    companyId: string,
    integrationType: 'financial' | 'hr',
    config: Partial<IntegrationConfig>
  ): Promise<IntegrationConfig> {
    // TODO: Replace with actual API call
    // return authFetch(`/api/companies/${companyId}/integrations/${integrationType}`, {
    //   method: 'PUT',
    //   body: JSON.stringify(config)
    // });
    const company = mockCompanies.find((c) => c.id === companyId);
    if (!company) throw new Error('Company not found');

    const integration: IntegrationConfig = {
      id: `${integrationType}-${companyId}-${Date.now()}`,
      companyId,
      systemType: config.systemType || '',
      systemName: config.systemName || '',
      connectionStatus: config.connectionStatus || 'not_configured',
      apiEndpoint: config.apiEndpoint || null,
      apiKey: config.apiKey || null,
      lastSync: null,
      lastTestDate: null,
      lastTestStatus: null,
      syncFrequency: config.syncFrequency || 'manual',
      autoSync: config.autoSync || false,
      config: config.config || {},
      errorMessage: null,
    };

    if (integrationType === 'financial') {
      company.financialIntegration = integration;
    } else {
      company.hrIntegration = integration;
    }

    return Promise.resolve(integration);
  },

  // Test integration connection
  async testConnection(data: TestConnectionRequest): Promise<TestConnectionResponse> {
    // TODO: Replace with actual API call
    // return authFetch('/api/integrations/test', { method: 'POST', body: JSON.stringify(data) });
    
    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Mock success response
    return Promise.resolve({
      success: true,
      message: 'Connection successful',
      details: {
        systemVersion: '2024.1',
        companyName: 'Test Company',
        availableModules: ['Projects', 'Timesheets', 'Expenses'],
      },
    });
  },

  // Trigger manual sync
  async triggerSync(data: SyncRequest): Promise<SyncResponse> {
    // TODO: Replace with actual API call
    // return authFetch('/api/integrations/sync', { method: 'POST', body: JSON.stringify(data) });
    
    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const company = mockCompanies.find((c) => c.id === data.companyId);
    if (!company) throw new Error('Company not found');

    // Update last sync time
    const integration = data.integrationType === 'financial' 
      ? company.financialIntegration 
      : company.hrIntegration;
    
    if (integration) {
      integration.lastSync = new Date();
      integration.connectionStatus = 'connected';
    }

    company.lastSync = new Date();

    return Promise.resolve({
      success: true,
      message: 'Sync completed successfully',
      recordsImported: 45,
      recordsUpdated: 12,
      recordsFailed: 0,
      duration: 2000,
    });
  },
};
